<script lang="ts" setup>
  import { computed, useId, useSlots } from 'vue';
  import { FontIcon } from '../FontIcon';
  import {
    containerClass,
    containerSizeClass,
    containerVariantClass,
    contentClass,
    contentSizeClass,
    ctaClass,
    descriptionClass,
    iconClass,
    ribbonClass,
    ribbonSizeClass,
    rowClass,
    rowSizeClass,
    titleClass,
  } from './Callout.css';
  import type { CalloutProps } from './Callout.types';
  import { CalloutType, CalloutVariant } from './Callout.types';

  const props = withDefaults(defineProps<CalloutProps>(), {
    type: CalloutType.INFO,
    variant: CalloutVariant.INFORMATIVE,
  });

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-callout-${uid}`);

  const containerClasses = computed(() => [
    containerClass,
    containerSizeClass[props.variant],
    containerVariantClass[props.type],
  ]);

  const isAssertive = computed(
    () =>
      props.type === CalloutType.ERROR || props.type === CalloutType.WARNING,
  );

  const ariaRole = computed(() => (isAssertive.value ? 'alert' : 'status'));

  const ariaLive = computed(() => (isAssertive.value ? 'assertive' : 'polite'));

  const slots = useSlots();

  const hasRibbon = computed(() => !!slots.ribbon);
</script>

<template>
  <div
    :id="id"
    :class="containerClasses"
    :role="ariaRole"
    :aria-live="ariaLive"
    :data-testid="resolvedTestId"
  >
    <div
      v-if="hasRibbon"
      :class="[ribbonClass, ribbonSizeClass[variant]]"
      :data-testid="`${resolvedTestId}-ribbon`"
    >
      <slot name="ribbon" />
    </div>

    <div :class="[rowClass, rowSizeClass[variant]]">
      <span
        v-if="$slots.icon || iconName"
        :class="iconClass"
        aria-hidden="true"
        :data-testid="`${resolvedTestId}-icon`"
      >
        <slot name="icon">
          <FontIcon v-if="iconName" :icon-name="iconName" />
        </slot>
      </span>

      <div :class="[contentClass, contentSizeClass[variant]]">
        <p
          v-if="title || $slots.title"
          :class="titleClass[variant]"
          :data-testid="`${resolvedTestId}-title`"
        >
          <slot name="title">{{ title }}</slot>
        </p>
        <p
          v-if="description || $slots.description"
          :class="descriptionClass[variant]"
          :data-testid="`${resolvedTestId}-description`"
        >
          <slot name="description">{{ description }}</slot>
        </p>
        <div
          v-if="$slots.cta"
          :class="ctaClass"
          :data-testid="`${resolvedTestId}-cta`"
        >
          <slot name="cta" />
        </div>
      </div>
    </div>
  </div>
</template>
